import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { z } from 'zod';

import './styles.css';

import { UpdatePrompt } from './components/UpdatePrompt';
import { AuthProvider, useAuth } from './lib/auth';
import { captureAuthorizationRequest } from './lib/oauthRequest';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import { spanishMessage } from './lib/zodMessages';

// La pantalla de consentimiento OAuth necesita la query tal y como la escribió
// el cliente, y el router la normaliza al montarse: se guarda antes.
captureAuthorizationRequest(window.location);

// Mensajes de validación en español, también para los esquemas de
// `@fitness/core` (comparten la misma instancia de zod en el bundle).
// `localeError` cubre todos los casos; `customError` reescribe en castellano
// llano los que más ve el usuario.
z.config({ ...z.locales.es(), customError: spanishMessage });

/**
 * Puente entre el estado de sesión y el router: `AuthProvider` queda por
 * encima para que `beforeLoad` pueda leerlo a través del contexto.
 */
function RoutedApp() {
  const auth = useAuth();

  useEffect(() => {
    // El contexto del router no es reactivo por sí solo: al cambiar la sesión
    // hay que pedirle que vuelva a evaluar las guardas de ruta.
    void router.invalidate();
  }, [auth.status]);

  return <RouterProvider router={router} context={{ auth }} />;
}

const container = document.getElementById('root');
if (container === null) throw new Error('No se ha encontrado el contenedor #root.');

// Vite puede reejecutar este módulo al recargar en caliente; reutilizar la raíz
// evita el aviso de React por llamar dos veces a `createRoot()` sobre el mismo
// contenedor. En producción el módulo se ejecuta una única vez.
const holder = container as HTMLElement & { __fitnessRoot?: ReturnType<typeof createRoot> };
holder.__fitnessRoot ??= createRoot(container);

holder.__fitnessRoot.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RoutedApp />
        <UpdatePrompt />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
